import { type ReactNode, useEffect } from 'react'
import { computeGuardAction, navigate } from '@utils/route'

export interface RouteGuardProps {
  route: string

  /** 認証状態。親側で確実に渡す (props drilling は 1 段のみで止まる)。 */
  isAuthenticated: boolean

  /** LOCKED 状態かどうか。LOCKED + 保護ルートなら `#lock` に誘導する (L-18a)。 */
  isLocked: boolean
  children: ReactNode
}

/**
 * 非 AUTH_ROUTES + 未認証 → '#login'、LOCKED → '#lock' に強制 navigate して null を返す。
 *
 * 副作用は useEffect 内で発火させ、SSR 安全 / React 18 strict-mode の二重呼出にも
 * 耐える (navigate は同一 hash でショートサーキット)。
 *
 * sessionStore に直接依存させない理由:
 *   - テスト容易性 (jsdom セッション初期化が不要)
 *   - 親 App.tsx で 1 度だけセッションを読み、複数 Guard を呼ぶ将来構成に対応
 */
export const RouteGuard = ({
  route,
  isAuthenticated,
  isLocked,
  children,
}: RouteGuardProps): ReactNode => {
  const action = computeGuardAction(route, isAuthenticated, isLocked)
  const redirectTo = action.kind === 'redirect' ? action.to : null

  useEffect(() => {
    if (redirectTo !== null) navigate(redirectTo)
  }, [redirectTo])

  if (redirectTo !== null) return null

  return children
}
